// ============================================
// pages/society/SocietyIncoming.tsx
// Trucks dispatched by suppliers landing at this PACS — pending acknowledge.
// ============================================
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle, CheckCircle, CheckCircle2, Eye, Package, Truck, XCircle } from 'lucide-react';
import { gunnyPosAPI, seasonsAPI, commoditiesAPI } from '../../api/services';
import apiClient from '../../api/apiClient';
import { bagTypeLabel } from '../../components/gunny/bagTypes';
import { Pagination } from '../../components/common/Pagination/Pagination';
import { usePagination } from '../../components/common/Pagination/usePagination';

export const SocietyIncoming: React.FC = () => {
  const [seasonId, setSeasonId] = useState<number | null>(null);
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  // A trip is the set of in-transit deliveries that share a (vehicle_number,
  // dispatch_date) — they arrived on the same truck and the PACS officer
  // confirms them together. ackTrip holds the full list; ackInputs maps each
  // delivery_id to its received_bags + per-line remarks.
  const [ackTrip, setAckTrip] = useState<any[] | null>(null);
  const [ackInputs, setAckInputs] = useState<Record<number, { received_bags: number; received_remarks: string }>>({});
  const [tripRemarks, setTripRemarks] = useState<string>('');
  const [saving, setSaving] = useState(false);
  // Read-only "View details" of any single lot (in-transit or received).
  const [viewLot, setViewLot] = useState<any | null>(null);
  // Reject one specific lot — supplies a reason that goes into rejection_reason.
  const [rejectLot, setRejectLot] = useState<any | null>(null);
  const [rejectReason, setRejectReason] = useState<string>('');
  // Filters
  const [commodities, setCommodities] = useState<any[]>([]);
  const [statusFilter, setStatusFilter] = useState<'all' | 'in_transit' | 'received' | 'rejected' | 'awaiting_dm' | 'dm_confirmed'>('all');
  const [commodityFilter, setCommodityFilter] = useState<number>(0);
  const [search, setSearch] = useState<string>('');

  const load = useCallback(async (sid: number) => {
    setLoading(true); setErr(null);
    try {
      const { data } = await gunnyPosAPI.incomingForSociety(sid);
      setRows(data);
    } catch (e: any) {
      setErr(e?.response?.data?.message || 'Failed to load incoming dispatches');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    seasonsAPI.active().then(({ data }) => {
      if (data?.id) { setSeasonId(data.id); load(data.id); }
    }).catch(() => setLoading(false));
    commoditiesAPI.list().then(({ data }) => {
      setCommodities(Array.isArray(data) ? data : []);
    }).catch(() => {});
  }, [load]);

  // Group all in-transit deliveries that share the same trip key with the
  // clicked one (same vehicle_number + dispatch_date). Backend stores each
  // delivery row independently — we just bundle the UX of confirming receipt.
  const tripKey = (d: any) => `${(d.vehicle_number || '').trim()}|${d.dispatch_date || d.delivered_on || ''}`;
  const openAck = (d: any) => {
    const key = tripKey(d);
    const trip = rows
      .filter((r) => !r.received_at && tripKey(r) === key)
      .sort((a, b) => a.id - b.id);
    const inputs: Record<number, { received_bags: number; received_remarks: string }> = {};
    for (const t of trip) inputs[t.id] = { received_bags: Number(t.bags) || 0, received_remarks: '' };
    setAckTrip(trip);
    setAckInputs(inputs);
    setTripRemarks('');
  };

  const submit = async () => {
    if (!seasonId || !ackTrip || ackTrip.length === 0) return;
    setSaving(true); setErr(null);
    const succeeded: number[] = [];
    const failed: { id: number; msg: string }[] = [];
    for (const t of ackTrip) {
      const i = ackInputs[t.id];
      if (!i || i.received_bags <= 0) continue;
      try {
        await gunnyPosAPI.acknowledgeDelivery(seasonId, t.po_id, t.id, {
          received_bags: i.received_bags,
          received_remarks: (i.received_remarks?.trim() || tripRemarks?.trim() || undefined) as any,
        });
        succeeded.push(t.id);
      } catch (e: any) {
        failed.push({ id: t.id, msg: e?.response?.data?.message || 'failed' });
      }
    }
    setSaving(false);
    if (failed.length > 0) {
      setErr(`Acknowledged ${succeeded.length} of ${ackTrip.length}. Failed: ${failed.map((f) => `#${f.id} (${f.msg})`).join('; ')}`);
    } else {
      const veh = ackTrip[0].vehicle_number || '—';
      const totalBags = succeeded.reduce((s, id) => s + (ackInputs[id]?.received_bags || 0), 0);
      setFlash(`Acknowledged ${succeeded.length} line${succeeded.length === 1 ? '' : 's'} from vehicle ${veh} — ${totalBags.toLocaleString()} bags total. Rows created in My Consignments.`);
      setAckTrip(null); setAckInputs({}); setTripRemarks('');
      setTimeout(() => setFlash(null), 4000);
    }
    await load(seasonId);
  };

  const pending = rows.filter((d) => !d.received_at).length;

  // Filter pipeline: status + commodity + free-text search
  const filteredRows = useMemo(() => {
    const s = search.trim().toLowerCase();
    return rows.filter((d: any) => {
      if (statusFilter !== 'all') {
        if (statusFilter === 'in_transit'   && (d.received_at || d.rejected_at)) return false;
        if (statusFilter === 'rejected'     && !d.rejected_at) return false;
        if (statusFilter === 'received'     && !(d.received_at && !d.rejected_at)) return false;
        if (statusFilter === 'awaiting_dm'  && !(d.received_at && !d.rejected_at && !d.utilization_receipt?.verified_at && !d.utilization_receipt?.rejected_at)) return false;
        if (statusFilter === 'dm_confirmed' && !d.utilization_receipt?.verified_at) return false;
      }
      if (commodityFilter > 0) {
        const cid = (d.poItem?.commodity?.id) || null;
        if (cid !== commodityFilter) return false;
      }
      if (s) {
        const blob = `${d.po?.po_number || ''} ${d.vehicle_number || ''} ${d.supplier_invoice_no || ''} ${d.eway_bill_no || ''} ${d.poItem?.bag_type || ''} ${d.poItem?.commodity?.name || ''}`.toLowerCase();
        if (!blob.includes(s)) return false;
      }
      return true;
    });
  }, [rows, statusFilter, commodityFilter, search]);

  const { page, pageSize, totalPages, totalItems, pagedItems, setPage, setPageSize } =
    usePagination(filteredRows, 25);

  const submitReject = async () => {
    if (!seasonId || !rejectLot) return;
    if (!rejectReason.trim()) { setErr('Reason is required'); return; }
    setSaving(true); setErr(null);
    try {
      await gunnyPosAPI.rejectDelivery(seasonId, rejectLot.po_id, rejectLot.id, { reason: rejectReason.trim() });
      setFlash(`Rejected lot from vehicle ${rejectLot.vehicle_number || '—'}. Supplier's allotment cap is freed.`);
      setRejectLot(null); setRejectReason('');
      await load(seasonId);
      setTimeout(() => setFlash(null), 3500);
    } catch (e: any) {
      setErr(e?.response?.data?.message || 'Reject failed');
    } finally { setSaving(false); }
  };

  const downloadInvoice = async (poId: number, deliveryId: number, invoiceId: number, filename: string) => {
    if (!seasonId) return;
    try {
      const res = await apiClient.get(
        `/gunny-pos/${seasonId}/${poId}/deliveries/${deliveryId}/invoices/${invoiceId}`,
        { responseType: 'blob' },
      );
      const url = window.URL.createObjectURL(res.data as Blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
    } catch (e: any) {
      setErr(e?.response?.data?.message || 'Could not download invoice');
    }
  };

  return (
    <div>
      <div className="mb-4">
        <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
          <Truck className="w-5 h-5 text-blue-600" /> Incoming Lots
          {pending > 0 && <span className="px-2 py-0.5 text-xs bg-amber-100 text-amber-800 rounded-full">{pending} pending</span>}
        </h1>
        <p className="text-sm text-gray-600 mt-1">Vehicles dispatched by suppliers to your PACS. Acknowledge each on arrival to create a consignment row in My Consignments.</p>
      </div>

      <div className="mb-4 bg-white rounded-xl border border-gray-200 p-3 flex flex-wrap gap-2 items-center">
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as any)}
          className="px-3 py-1.5 text-sm border rounded bg-white">
          <option value="all">All statuses</option>
          <option value="in_transit">In transit (needs ack)</option>
          <option value="received">Received</option>
          <option value="awaiting_dm">Received — awaiting DM confirm</option>
          <option value="dm_confirmed">Received — DM confirmed</option>
          <option value="rejected">Rejected</option>
        </select>
        <select value={commodityFilter} onChange={(e) => setCommodityFilter(parseInt(e.target.value) || 0)}
          className="px-3 py-1.5 text-sm border rounded bg-white">
          <option value={0}>All commodities</option>
          {commodities.map((c: any) => (<option key={c.id} value={c.id}>{c.name}</option>))}
        </select>
        <input type="text" placeholder="Search PO no / vehicle / invoice / e-way / bag…" value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="px-3 py-1.5 text-sm border rounded flex-1 min-w-[200px]" />
        <span className="text-xs text-gray-500">{filteredRows.length} of {rows.length} lots</span>
      </div>

      {err && <div className="mb-3 flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm"><AlertCircle className="w-4 h-4" /> {err}</div>}
      {flash && <div className="mb-3 flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded text-green-700 text-sm"><CheckCircle className="w-4 h-4" /> {flash}</div>}

      <div className="bg-white rounded-xl shadow border border-gray-200 overflow-hidden">
        {loading ? <div className="p-10 text-center text-gray-500">Loading…</div>
          : rows.length === 0 ? <div className="p-10 text-center text-gray-500">No supplier vehicles dispatched to your PACS yet.</div>
          : filteredRows.length === 0 ? <div className="p-10 text-center text-gray-500">No lots match the current filters.</div>
          : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr className="text-left text-xs uppercase text-gray-600">
                  <th className="px-3 py-2">PO</th>
                  <th className="px-3 py-2">What's in the lot</th>
                  <th className="px-3 py-2">Vehicle</th>
                  <th className="px-3 py-2">Dispatched</th>
                  <th className="px-3 py-2 text-right">Bags</th>
                  <th className="px-3 py-2">E-Way</th>
                  <th className="px-3 py-2">Invoice No.</th>
                  <th className="px-3 py-2">Files</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {pagedItems.map((d: any) => (
                  <tr key={d.id} className="border-b last:border-0 align-top">
                    <td className="px-3 py-2 font-mono text-xs">{d.po?.po_number || `po#${d.po_id}`}</td>
                    <td className="px-3 py-2 text-xs">
                      {d.poItem ? (
                        <div className="flex flex-col gap-0.5">
                          {d.poItem.commodity?.name && (
                            <span className="inline-block px-1.5 py-0.5 bg-emerald-50 text-emerald-700 rounded text-[10px] w-fit">{d.poItem.commodity.name}</span>
                          )}
                          <span className="text-gray-800">{bagTypeLabel(d.poItem.bag_type)}</span>
                          {d.poItem.specification && <span className="text-[10px] text-gray-500">{d.poItem.specification}</span>}
                        </div>
                      ) : <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">{d.vehicle_number || '—'}</td>
                    <td className="px-3 py-2 text-xs">{d.dispatch_date || d.delivered_on}</td>
                    <td className="px-3 py-2 text-right tabular-nums font-medium">{Number(d.bags).toLocaleString()}</td>
                    <td className="px-3 py-2 text-xs">{d.eway_bill_no || '—'}</td>
                    <td className="px-3 py-2 text-xs">{d.supplier_invoice_no || '—'}</td>
                    <td className="px-3 py-2 text-xs">
                      {(d.invoices || []).length === 0 ? (
                        <span className="text-gray-400">—</span>
                      ) : (
                        <div className="flex flex-col gap-1 max-w-[220px]">
                          {(d.invoices as any[]).map((inv) => (
                            <button key={inv.id}
                              onClick={() => downloadInvoice(d.po_id, d.id, inv.id, inv.file_name)}
                              title={`Download ${inv.file_name}`}
                              className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 rounded text-[11px] text-left">
                              <span>📎</span>
                              <span className="truncate">{inv.file_name.length > 22 ? inv.file_name.slice(0, 19) + '…' : inv.file_name}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {d.rejected_at ? (
                        <span title={d.rejection_reason || ''} className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-rose-100 text-rose-800">
                          <XCircle className="w-3 h-3" /> Rejected
                        </span>
                      ) : d.received_at ? (
                        <div className="flex flex-col gap-0.5">
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-emerald-100 text-emerald-800 w-fit">
                            <CheckCircle2 className="w-3 h-3" /> Received {Number(d.received_bags).toLocaleString()}
                          </span>
                          {d.utilization_receipt?.verified_at ? (
                            <span title={`Verified ${new Date(d.utilization_receipt.verified_at).toLocaleString()}`}
                              className="inline-flex items-center gap-1 text-[10px] text-emerald-700 w-fit">
                              ✓ DM confirmed
                            </span>
                          ) : d.utilization_receipt?.rejected_at ? (
                            <span title={d.utilization_receipt.rejection_reason || ''}
                              className="inline-flex items-center gap-1 text-[10px] text-rose-700 w-fit">
                              ⚠ DM rejected — correct in My Consignments
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-[10px] text-amber-700 w-fit">
                              awaiting DM confirm
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="px-2 py-0.5 rounded text-xs bg-amber-100 text-amber-800">In transit</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="inline-flex items-center gap-1 flex-wrap justify-end">
                        <button onClick={() => setViewLot(d)} title="View details"
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded">
                          <Eye className="w-3 h-3" /> View
                        </button>
                        {!d.received_at && !d.rejected_at && (
                          <>
                            <button onClick={() => openAck(d)} title="Acknowledge receipt"
                              className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-emerald-600 hover:bg-emerald-700 text-white rounded">
                              <CheckCircle2 className="w-3 h-3" /> Acknowledge
                            </button>
                            <button onClick={() => { setRejectLot(d); setRejectReason(''); }} title="Reject this lot"
                              className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-rose-600 hover:bg-rose-700 text-white rounded">
                              <XCircle className="w-3 h-3" /> Reject
                            </button>
                          </>
                        )}
                        {d.received_at && !d.rejected_at && (
                          <Link to="/society/consignments" title="Open consignments"
                            className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-blue-50 hover:bg-blue-100 text-blue-700 rounded">
                            <Package className="w-3 h-3" /> Consignment
                          </Link>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )}
        {!loading && filteredRows.length > 0 && totalPages > 1 && (
          <div className="px-3 py-3 border-t bg-gray-50">
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              onPageChange={setPage}
              totalItems={totalItems}
              pageSize={pageSize}
              onPageSizeChange={setPageSize}
              itemLabel="lots"
            />
          </div>
        )}
      </div>

      {ackTrip && ackTrip.length > 0 && (() => {
        const head = ackTrip[0];
        const allFiles: { delivery_id: number; po_id: number; inv: any }[] = [];
        for (const t of ackTrip) for (const inv of (t.invoices || [])) allFiles.push({ delivery_id: t.id, po_id: t.po_id, inv });
        const totalDispatched = ackTrip.reduce((s, t) => s + Number(t.bags || 0), 0);
        const totalGot = ackTrip.reduce((s, t) => s + Number(ackInputs[t.id]?.received_bags || 0), 0);
        const totalShort = Math.max(totalDispatched - totalGot, 0);
        const anyOver = ackTrip.some((t) => (ackInputs[t.id]?.received_bags || 0) > Number(t.bags || 0));
        const setRow = (id: number, patch: Partial<{ received_bags: number; received_remarks: string }>) =>
          setAckInputs((cur) => ({ ...cur, [id]: { ...cur[id], ...patch } as any }));
        return (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[92vh] overflow-y-auto">
              <div className="px-6 py-4 border-b sticky top-0 bg-white">
                <h2 className="font-semibold text-gray-900 flex items-center gap-2">
                  <Truck className="w-5 h-5 text-blue-600" /> Acknowledge incoming trip — {ackTrip.length} line{ackTrip.length === 1 ? '' : 's'}
                </h2>
                <p className="text-xs text-gray-500 mt-0.5">
                  Same vehicle, same dispatch date — confirm received bags per line. Each line creates its own consignment row.
                </p>
              </div>

              <div className="px-6 py-5 space-y-5">
                {/* Trip details — single block since vehicle/e-way/invoice/date are shared across the trip */}
                <section>
                  <div className="text-[10px] uppercase tracking-wide text-gray-500 mb-1.5">Trip details (from supplier)</div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 p-3 border border-gray-200 rounded-lg text-sm">
                    <div>
                      <div className="text-[11px] text-gray-500">Vehicle no.</div>
                      <div className="font-mono text-gray-900">{head.vehicle_number || '—'}</div>
                    </div>
                    <div>
                      <div className="text-[11px] text-gray-500">Dispatch date</div>
                      <div className="text-gray-900">{head.dispatch_date || head.delivered_on || '—'}</div>
                    </div>
                    <div>
                      <div className="text-[11px] text-gray-500">E-way bill</div>
                      <div className="font-mono text-gray-900 break-all">{head.eway_bill_no || '—'}</div>
                    </div>
                    <div>
                      <div className="text-[11px] text-gray-500">Invoice no.</div>
                      <div className="font-mono text-gray-900 break-all">{head.supplier_invoice_no || '—'}</div>
                    </div>
                    <div>
                      <div className="text-[11px] text-gray-500">Invoice date</div>
                      <div className="text-gray-900">{head.supplier_invoice_date || '—'}</div>
                    </div>
                    <div>
                      <div className="text-[11px] text-gray-500">PO</div>
                      <div className="font-mono text-gray-900">{head.po?.po_number || `po#${head.po_id}`}</div>
                    </div>
                  </div>
                </section>

                {/* Files attached by supplier (across all lines on this trip) */}
                <section>
                  <div className="text-[10px] uppercase tracking-wide text-gray-500 mb-1.5">Invoice file(s) attached</div>
                  {allFiles.length === 0 ? (
                    <div className="p-3 border border-gray-200 border-dashed rounded text-xs text-gray-500 italic">No files uploaded by supplier.</div>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {allFiles.map(({ delivery_id, po_id, inv }) => (
                        <button key={`${delivery_id}-${inv.id}`}
                          onClick={() => downloadInvoice(po_id, delivery_id, inv.id, inv.file_name)}
                          title={`Download ${inv.file_name}`}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 rounded text-xs">
                          <span>📎</span>
                          <span className="truncate max-w-[180px]">{inv.file_name}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </section>

                {/* Per-line receipt entry — one row per delivery on the trip */}
                <section className="pt-2 border-t">
                  <div className="text-[10px] uppercase tracking-wide text-gray-500 mb-1.5">Receipt at PACS — per line</div>
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 border-b">
                        <tr className="text-left text-xs uppercase text-gray-600">
                          <th className="px-3 py-2">Commodity / Bag</th>
                          <th className="px-3 py-2 text-right">Rate</th>
                          <th className="px-3 py-2 text-right">Dispatched</th>
                          <th className="px-3 py-2 text-right w-32">Received</th>
                          <th className="px-3 py-2">Remarks (line)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ackTrip.map((t) => {
                          const rowGot = Number(ackInputs[t.id]?.received_bags || 0);
                          const dispatched = Number(t.bags || 0);
                          const rowShort = Math.max(dispatched - rowGot, 0);
                          const rowOver = rowGot > dispatched;
                          const rate = Number(t.poItem?.rate || 0);
                          return (
                            <tr key={t.id} className="border-b last:border-0 align-top">
                              <td className="px-3 py-2">
                                <div className="flex flex-wrap items-center gap-2">
                                  {t.poItem?.commodity?.name && (
                                    <span className="inline-block px-1.5 py-0.5 bg-emerald-50 text-emerald-700 rounded text-[10px] font-medium">{t.poItem.commodity.name}</span>
                                  )}
                                  <span className="text-gray-900 font-medium text-sm">{t.poItem ? bagTypeLabel(t.poItem.bag_type) : '—'}</span>
                                </div>
                                {t.poItem?.specification && <div className="text-[11px] text-gray-500 mt-0.5">{t.poItem.specification}</div>}
                              </td>
                              <td className="px-3 py-2 text-right tabular-nums text-gray-700 text-xs">
                                {rate > 0 ? `₹ ${rate.toFixed(2)}` : '—'}
                              </td>
                              <td className="px-3 py-2 text-right tabular-nums font-medium">{dispatched.toLocaleString()}</td>
                              <td className="px-3 py-2">
                                <input type="number" min={0} max={dispatched}
                                  value={ackInputs[t.id]?.received_bags ?? ''}
                                  onChange={(e) => setRow(t.id, { received_bags: Number(e.target.value) || 0 })}
                                  className={`w-full px-2 py-1.5 border-2 rounded text-sm font-semibold tabular-nums text-right focus:outline-none ${
                                    rowOver ? 'border-rose-400 bg-rose-50' :
                                    rowShort > 0 ? 'border-amber-400 bg-amber-50' :
                                    rowGot > 0 ? 'border-emerald-400 bg-emerald-50' : 'border-gray-200'
                                  }`} />
                                {rowShort > 0 && rowGot > 0 && (
                                  <div className="text-[10px] text-amber-700 mt-0.5">Short by {rowShort.toLocaleString()}</div>
                                )}
                                {rowOver && (
                                  <div className="text-[10px] text-rose-600 mt-0.5">Exceeds dispatched</div>
                                )}
                              </td>
                              <td className="px-3 py-2">
                                <input type="text" value={ackInputs[t.id]?.received_remarks || ''}
                                  onChange={(e) => setRow(t.id, { received_remarks: e.target.value })}
                                  placeholder="optional, e.g. 5 bags torn"
                                  className="w-full px-2 py-1 border rounded text-xs" />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot className="bg-gray-50 border-t-2">
                        <tr>
                          <td colSpan={2} className="px-3 py-2 text-right font-semibold text-gray-700">Total</td>
                          <td className="px-3 py-2 text-right tabular-nums font-semibold text-gray-700">{totalDispatched.toLocaleString()}</td>
                          <td className="px-3 py-2 text-right tabular-nums font-semibold text-gray-900">{totalGot.toLocaleString()}</td>
                          <td className="px-3 py-2 text-xs">
                            {totalShort > 0 && totalGot > 0 && (
                              <span className="text-amber-700">Short by {totalShort.toLocaleString()} total</span>
                            )}
                            {totalGot > 0 && totalGot === totalDispatched && (
                              <span className="text-emerald-700">✓ Full receipt</span>
                            )}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>

                  <div className="mt-3">
                    <label className="block text-xs font-medium text-gray-700 mb-1">Trip-level remarks (optional, applied to lines that don't have their own)</label>
                    <textarea rows={2} value={tripRemarks}
                      onChange={(e) => setTripRemarks(e.target.value)}
                      className="w-full px-3 py-2 border rounded text-sm"
                      placeholder="e.g. Truck arrived 14:30, gate-pass #1234, driver — Suresh" />
                  </div>
                </section>
              </div>

              <div className="px-6 py-4 border-t flex justify-end gap-2 bg-gray-50 sticky bottom-0">
                <button onClick={() => { setAckTrip(null); setAckInputs({}); setTripRemarks(''); }}
                  className="px-4 py-2 text-sm hover:bg-gray-100 rounded">Cancel</button>
                <button onClick={submit}
                  disabled={saving || totalGot <= 0 || anyOver}
                  className="px-4 py-2 text-sm bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded">
                  {saving ? 'Acknowledging…' : `Acknowledge ${ackTrip.length} line${ackTrip.length === 1 ? '' : 's'} · ${totalGot.toLocaleString()} bags`}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {viewLot && (() => {
        const lot = viewLot;
        const rate = Number(lot.poItem?.rate || 0);
        return (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto">
              <div className="px-6 py-4 border-b sticky top-0 bg-white">
                <h2 className="font-semibold text-gray-900 flex items-center gap-2">
                  <Eye className="w-5 h-5 text-gray-600" /> Lot details — {lot.po?.po_number || `PO #${lot.po_id}`}
                </h2>
                <p className="text-xs text-gray-500 mt-0.5">Read-only view of what the supplier dispatched and how it was received.</p>
              </div>
              <div className="px-6 py-5 space-y-5">
                <section>
                  <div className="text-[10px] uppercase tracking-wide text-gray-500 mb-1.5">What's in the lot</div>
                  <div className="p-3 border border-gray-200 rounded-lg bg-gray-50">
                    {lot.poItem ? (
                      <div className="flex flex-wrap items-center gap-3">
                        {lot.poItem.commodity?.name && (
                          <span className="inline-block px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded font-medium text-sm">{lot.poItem.commodity.name}</span>
                        )}
                        <span className="text-gray-900 font-medium">{bagTypeLabel(lot.poItem.bag_type)}</span>
                        {lot.poItem.specification && <span className="text-sm text-gray-600">· {lot.poItem.specification}</span>}
                        {rate > 0 && (
                          <span className="ml-auto text-xs text-gray-600">Rate <span className="font-medium text-gray-800">₹ {rate.toFixed(2)}</span> / bag</span>
                        )}
                      </div>
                    ) : <span className="text-sm text-gray-500">Lot details unavailable (legacy delivery).</span>}
                  </div>
                </section>
                <section>
                  <div className="text-[10px] uppercase tracking-wide text-gray-500 mb-1.5">Trip details (from supplier)</div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 p-3 border border-gray-200 rounded-lg text-sm">
                    <div><div className="text-[11px] text-gray-500">Vehicle no.</div><div className="font-mono text-gray-900">{lot.vehicle_number || '—'}</div></div>
                    <div><div className="text-[11px] text-gray-500">Dispatch date</div><div className="text-gray-900">{lot.dispatch_date || lot.delivered_on || '—'}</div></div>
                    <div><div className="text-[11px] text-gray-500">E-way bill</div><div className="font-mono text-gray-900 break-all">{lot.eway_bill_no || '—'}</div></div>
                    <div><div className="text-[11px] text-gray-500">Invoice no.</div><div className="font-mono text-gray-900 break-all">{lot.supplier_invoice_no || '—'}</div></div>
                    <div><div className="text-[11px] text-gray-500">Invoice date</div><div className="text-gray-900">{lot.supplier_invoice_date || '—'}</div></div>
                    <div><div className="text-[11px] text-gray-500">Bags dispatched</div><div className="text-gray-900 tabular-nums font-semibold">{Number(lot.bags || 0).toLocaleString()}</div></div>
                  </div>
                </section>
                <section>
                  <div className="text-[10px] uppercase tracking-wide text-gray-500 mb-1.5">Invoice file(s)</div>
                  {(lot.invoices || []).length === 0 ? (
                    <div className="p-3 border border-gray-200 border-dashed rounded text-xs text-gray-500 italic">No file uploaded by supplier.</div>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {(lot.invoices as any[]).map((inv) => (
                        <button key={inv.id} onClick={() => downloadInvoice(lot.po_id, lot.id, inv.id, inv.file_name)}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 rounded text-xs">
                          <span>📎</span><span className="truncate max-w-[200px]">{inv.file_name}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </section>
                <section>
                  <div className="text-[10px] uppercase tracking-wide text-gray-500 mb-1.5">Receipt status</div>
                  <div className="p-3 border border-gray-200 rounded-lg text-sm">
                    {lot.rejected_at ? (
                      <div className="text-rose-700">
                        <div className="font-medium flex items-center gap-1"><XCircle className="w-4 h-4" /> Rejected on {new Date(lot.rejected_at).toLocaleString()}</div>
                        {lot.rejection_reason && <div className="mt-1 text-xs text-gray-700">Reason: {lot.rejection_reason}</div>}
                      </div>
                    ) : lot.received_at ? (
                      <div className="text-emerald-700">
                        <div className="font-medium flex items-center gap-1"><CheckCircle2 className="w-4 h-4" /> Received {Number(lot.received_bags || 0).toLocaleString()} bags on {new Date(lot.received_at).toLocaleString()}</div>
                        {lot.received_remarks && <div className="mt-1 text-xs text-gray-700">Remarks: {lot.received_remarks}</div>}
                      </div>
                    ) : (
                      <div className="text-amber-700 font-medium">In transit — awaiting receipt.</div>
                    )}
                  </div>
                </section>
              </div>
              <div className="px-6 py-4 border-t flex justify-end gap-2 bg-gray-50">
                <button onClick={() => setViewLot(null)} className="px-4 py-2 text-sm bg-gray-700 hover:bg-gray-800 text-white rounded">Close</button>
              </div>
            </div>
          </div>
        );
      })()}

      {rejectLot && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="px-6 py-4 border-b">
              <h2 className="font-semibold text-gray-900 flex items-center gap-2">
                <XCircle className="w-5 h-5 text-rose-600" /> Reject lot — {rejectLot.po?.po_number || `PO #${rejectLot.po_id}`}
              </h2>
              <p className="text-xs text-gray-500 mt-1">
                Vehicle <span className="font-mono">{rejectLot.vehicle_number || '—'}</span> · {Number(rejectLot.bags || 0).toLocaleString()} bags
                {rejectLot.poItem && <> · {bagTypeLabel(rejectLot.poItem.bag_type)}{rejectLot.poItem.commodity?.name ? ` · ${rejectLot.poItem.commodity.name}` : ''}</>}
              </p>
              <p className="text-xs text-rose-700 mt-2 bg-rose-50 border border-rose-200 rounded p-2">
                Rejecting frees the supplier's allotment cap so they can redispatch. This is permanent — use only when the lot truly cannot be received (truck never arrived, completely damaged, wrong PACS, fraudulent invoice).
              </p>
            </div>
            <div className="px-6 py-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Reason <span className="text-rose-600">*</span></label>
              <textarea rows={3} value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="e.g. Truck never arrived. Driver claims wrong PACS. Verified by manager (Mr. Suresh)."
                className="w-full px-3 py-2 border-2 border-rose-200 rounded text-sm focus:outline-none focus:border-rose-400" autoFocus />
            </div>
            <div className="px-6 py-4 border-t flex justify-end gap-2 bg-gray-50">
              <button onClick={() => { setRejectLot(null); setRejectReason(''); }}
                className="px-4 py-2 text-sm hover:bg-gray-100 rounded">Cancel</button>
              <button onClick={submitReject}
                disabled={saving || !rejectReason.trim()}
                className="px-4 py-2 text-sm bg-rose-600 hover:bg-rose-700 disabled:opacity-50 text-white rounded">
                {saving ? 'Rejecting…' : 'Reject lot'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SocietyIncoming;
